<script setup lang="ts">
import debounce from "lodash.debounce";

const { t } = useI18n();

type Props = {
  placeholder?: string;
  size?: "md" | "lg";
  modelValue?: string;
  showClearButton?: boolean;
  isDebouncing?: boolean;
  debounceDelay?: number;
};

const props = withDefaults(defineProps<Props>(), {
  placeholder: "",
  size: undefined,
  modelValue: "",
  showClearButton: false,
  isDebouncing: false,
  debounceDelay: 300,
});

const emit = defineEmits(["update:modelValue", "submit"]);

const debouncedEmit = debounce((value: string) => {
  emit("update:modelValue", value);
}, props.debounceDelay);

const handleInputEvent = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (props.isDebouncing) {
    debouncedEmit(target.value);
  } else {
    emit("update:modelValue", target.value);
  }
};

const clearSearch = () => {
  emit("update:modelValue", "");
};
</script>
<template>
  <div
    class="search-input__container"
    :class="{ 'search-input__container--lg': size === 'lg' }"
  >
    <div class="search-input__wrapper">
      <input
        :value="modelValue"
        type="search"
        :placeholder="placeholder"
        :aria-label="placeholder"
        class="form-control search-input__input"
        :class="{
          'form-control-lg': size === 'lg',
          'form-control-md': size === 'md',
          'search-input__input--clear': showClearButton,
        }"
        @input="handleInputEvent"
      />
      <button
        id="button-addon2"
        name="button"
        type="submit"
        class="search-input__button input-group-text"
        :aria-label="t('links.search')"
        @click="emit('submit')"
        @keyup.enter="emit('submit')"
      >
        <i class="bi bi-search"></i>
      </button>
      <button
        v-if="showClearButton && modelValue"
        id="button-addon3"
        name="clear-button"
        type="submit"
        class="search-input__button search-input__button--clear input-group-text"
        :aria-label="t('links.delete')"
        @click="clearSearch"
        @keyup.enter="clearSearch"
      >
        <i class="bi bi-x-lg"></i>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.search-input {
  &__container {
    width: 100%;
    display: flex;
    align-items: center;
  }

  &__wrapper {
    position: relative;
    width: 100%;
  }

  &__button {
    position: absolute;
    top: $input-btn-padding-y-sm;
    bottom: $input-btn-padding-y-sm;
    left: $input-btn-padding-y-sm;
    border: none;
    border-right: 0;
    color: $secondary;
    background-color: $color-white;
    border-radius: $border-radius-md;

    &--clear {
      right: $input-btn-padding-y-sm;
      left: auto;
    }

    &:hover {
      background: $hover-color;
    }
  }

  &__input {
    width: 100%;
    padding-left: 44px;
    border-radius: $border-radius-md;

    &--clear {
      padding-right: 44px;
    }
  }
}

/* clears the 'X' from Chrome */
input[type="search"]::-webkit-search-decoration,
input[type="search"]::-webkit-search-cancel-button,
input[type="search"]::-webkit-search-results-button,
input[type="search"]::-webkit-search-results-decoration {
  display: none;
}

.form-control-md {
  @include media-breakpoint-down(sm) {
    font-size: $font-size-sm;
  }
}
</style>
